/**
 * @file FloatingActions.js
 * @module src/js/components/FloatingActions
 * @description
 * **Component for floating action buttons (FABs) on the webpage.**
 * - Provides quick access to common actions like theme toggle, back to top, contact us, and report an issue.
 * - Adapts to light/dark themes and touch screen devices.
 * - Utilizes safe-area insets for better positioning on devices with notches or gesture areas.
 *
 * @version
 * <p>
 *  2025-11-29: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-01: Larger opacity when hovered in dark mode. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useState, useRef } from "react";
import { useTheme } from "../contexts/ThemeProvider";
import CircleButton from "./CircleButton";
import getDevice from "../functions/getDevice";
import { ArrowUp, Flag, MessageSquare, Sun, Moon } from "lucide-react";
import { Tooltip } from "react-tooltip";
import ReportAnIssue from "./ReportAnIssue";

export default function FloatingActions({ handleClickContactUs, isFloatingActionsVisible }) {
    const { theme, setTheme } = useTheme();
    const floatingActionsRef = useRef(null);
    const [hoverButtons, setHoverButtons] = useState(false);
    const { hasTouchScreen } = getDevice();

    // Feature-detect safe-area support (env() / constant())
    const supportsEnv = typeof CSS !== "undefined" && CSS.supports?.("padding: env(safe-area-inset-bottom)");
    const supportsConstant = typeof CSS !== "undefined" && CSS.supports?.("padding: constant(safe-area-inset-bottom)");

    // Utility: returns an appropriate safe-area expression or "0px"
    const safeArea = (edge) => (supportsEnv ? `env(${edge})` : (supportsConstant ? `constant(${edge})` : "0px"));

    useEffect(() => {
        if (!hasTouchScreen) return;
        const handleClickOutsideDropdown = (e) => {
            if (!floatingActionsRef.current?.contains(e.target)) {
                setHoverButtons(false);
            }
        };
        document.addEventListener("touchstart", handleClickOutsideDropdown);
        return () => document.removeEventListener("touchstart", handleClickOutsideDropdown);
    }, [hasTouchScreen]);

    return (
        <div
            ref={floatingActionsRef}
            role="group"
            aria-label="Quick actions"
            onMouseEnter={() => setHoverButtons(true)}
            onMouseLeave={() => setHoverButtons(false)}
            style={{
                position: "fixed",
                backgroundColor: theme === "dark" ? "transparent" : "#2e3842",
                padding: "6px",
                borderRadius: "12px",
                // Fallback for old browsers (always works)
                right: 16,
                bottom: 16,
                // Safe-area padding for notch/gesture areas (ignored if unsupported)
                paddingRight: `calc(6px + ${safeArea("safe-area-inset-right")})`,
                paddingBottom: `calc(6px + ${safeArea("safe-area-inset-bottom")})`,
                display: "grid",
                zIndex: 2000,               // ensure above overlays/menus
                pointerEvents: "auto",
                visibility: isFloatingActionsVisible ? "visible" : "hidden",
                opacity: isFloatingActionsVisible ? (hoverButtons ? 1.0 : theme === 'dark' ? 0.5 : 0.3) : 0,
                boxShadow: hoverButtons && theme === 'light' ? "0 12px 20px rgba(0,0,0,0.25)" : null,
                transition: "opacity 0.3s ease-in-out, visibility 0.3s ease-in-out, background-color 1s ease-in-out, box-shadow 0.3s ease-in-out",
            }}
        >
            <CircleButton
                tooltipId="theme-toggle-tooltip"
                tooltipMessage={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
                onClick={() => setTheme(prev => (prev === "dark" ? "light" : "dark"))}
                marginBottom="1em"
            >
                {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
            </CircleButton>
            <CircleButton
                tooltipId="back-to-top-tooltip"
                tooltipMessage="Back to top"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                marginBottom="1em"
            >
                <ArrowUp size={18} />
            </CircleButton>
            <CircleButton
                tooltipId="contact-us-tooltip"
                tooltipMessage="Contact us"
                onClick={handleClickContactUs}
                marginBottom="1em"
            >
                <MessageSquare size={16} />
            </CircleButton>
            <span
                data-tooltip-id="report-issue-tooltip"
                data-tooltip-content="Report an issue"
            >
                <ReportAnIssue>
                    <CircleButton
                        outline='2px solid lightcoral'
                        outlineOffset='-2px'
                    >
                        <Flag size={17} color="lightcoral" />
                    </CircleButton>
                </ReportAnIssue>
            </span>
            <Tooltip
                id="report-issue-tooltip"
                message="Report an issue"
            />
        </div>
    );
}
